import React, { useState, useEffect, useRef } from 'react';
import './Carousel.css';
import Card from '../Card/card';
import IconButton from '@mui/material/IconButton';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

const PAGE_WIDTH_PERCENT = 88;
const PEEK_PERCENT = 6;
const MOBILE_MQ = '(max-width: 600px)';
const WHEEL_DELTA_MIN = 20;
const WHEEL_COOLDOWN_MS = 500;
const ARROW_COOLDOWN_MS = 300;

const Carousel = ({ items, currentPage, onPageChange, onArrowClick, onCardClick }) => {
    const [itemsPerPage, setItemsPerPage] = useState(
        () => window.matchMedia(MOBILE_MQ).matches ? 1 : 3
    );
    const totalPages = Math.ceil(items.length / itemsPerPage);

    const [visualPage, setVisualPage] = useState(currentPage);
    const [skipTransition, setSkipTransition] = useState(false);

    const stageRef = useRef(null);
    const wheelCooldown = useRef(false);
    const arrowCooldown = useRef(false);
    const goNextRef = useRef(null);
    const goPrevRef = useRef(null);
    const isFirstRender = useRef(true);

    useEffect(() => {
        const mq = window.matchMedia(MOBILE_MQ);
        const handler = (e) => setItemsPerPage(e.matches ? 1 : 3);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);

    // Reset to page 0 when layout changes (mobile ↔ desktop)
    useEffect(() => {
        if (isFirstRender.current) { isFirstRender.current = false; return; }
        setVisualPage(0);
        onPageChange(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemsPerPage]);

    useEffect(() => {
        setVisualPage(currentPage);
    }, [currentPage]);

    // Re-enable transition after the instant snap-back has been painted
    useEffect(() => {
        if (skipTransition) {
            const id = requestAnimationFrame(() =>
                requestAnimationFrame(() => setSkipTransition(false))
            );
            return () => cancelAnimationFrame(id);
        }
    }, [skipTransition]);

    const isWrapping = visualPage < 0 || visualPage >= totalPages;
    // +1 because the extended array prepends a clone of the last page
    const translateX = PEEK_PERCENT - (visualPage + 1) * PAGE_WIDTH_PERCENT;

    const goPrev = () => {
        if (isWrapping || arrowCooldown.current) return;
        arrowCooldown.current = true;
        setTimeout(() => { arrowCooldown.current = false; }, ARROW_COOLDOWN_MS);
        onArrowClick?.();
        if (visualPage === 0) {
            setVisualPage(-1);
        } else {
            const prev = visualPage - 1;
            setVisualPage(prev);
            onPageChange(prev);
        }
    };

    const goNext = () => {
        if (isWrapping || arrowCooldown.current) return;
        arrowCooldown.current = true;
        setTimeout(() => { arrowCooldown.current = false; }, ARROW_COOLDOWN_MS);
        onArrowClick?.();
        if (visualPage === totalPages - 1) {
            setVisualPage(totalPages);
        } else {
            const next = visualPage + 1;
            setVisualPage(next);
            onPageChange(next);
        }
    };

    goNextRef.current = goNext;
    goPrevRef.current = goPrev;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const stage = stageRef.current;
        if (!stage) return;
        const handleWheel = (e) => {
            const delta = Math.abs(e.deltaX) >= Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
            if (Math.abs(delta) < WHEEL_DELTA_MIN) return;
            e.preventDefault();
            if (wheelCooldown.current) return;
            wheelCooldown.current = true;
            setTimeout(() => { wheelCooldown.current = false; }, WHEEL_COOLDOWN_MS);
            delta > 0 ? goNextRef.current() : goPrevRef.current();
        };
        stage.addEventListener('wheel', handleWheel, { passive: false });
        return () => stage.removeEventListener('wheel', handleWheel);
    }, []);

    const handleTransitionEnd = () => {
        if (visualPage === -1) {
            // Finished animating into clone-last → snap to real last with no transition
            setSkipTransition(true);
            setVisualPage(totalPages - 1);
            onPageChange(totalPages - 1);
        } else if (visualPage === totalPages) {
            // Finished animating into clone-first → snap to real first with no transition
            setSkipTransition(true);
            setVisualPage(0);
            onPageChange(0);
        }
    };

    // Build extended array: [clone-last, page0 ... pageN-1, clone-first]
    const pageGroups = Array.from({ length: totalPages }, (_, i) =>
        items.slice(i * itemsPerPage, i * itemsPerPage + itemsPerPage)
    );
    const extendedGroups = [pageGroups[totalPages - 1], ...pageGroups, pageGroups[0]];

    return (
        <div className="carousel">
            <div className="carousel__main">
                <IconButton onClick={goPrev} className="carousel__arrow">
                    <ArrowBackIosNewIcon sx={{ fontSize: 36 }} />
                </IconButton>
                <div
                    className="carousel__stage"
                    ref={stageRef}
                >
                    <div
                        className="carousel__track"
                        style={{
                            transform: `translateX(${translateX}%)`,
                            ...(skipTransition && { transition: 'none' }),
                        }}
                        onTransitionEnd={handleTransitionEnd}
                    >
                        {extendedGroups.map((pageItems, extIdx) => {
                            // extIdx 0 = clone-last (realPageIdx -1)
                            // extIdx 1..N = real pages (realPageIdx 0..N-1)
                            // extIdx N+1 = clone-first (realPageIdx N)
                            const realPageIdx = extIdx - 1;
                            const isActive = realPageIdx === visualPage;
                            return (
                                <div
                                    key={extIdx}
                                    className={`carousel__page${isActive ? ' carousel__page--active' : ''}`}
                                >
                                    {pageItems.map((cardData, i) => (
                                        <Card
                                            key={i}
                                            {...cardData}
                                            onClick={onCardClick}
                                        />
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                </div>
                <IconButton onClick={goNext} className="carousel__arrow">
                    <ArrowForwardIosIcon sx={{ fontSize: 36 }} />
                </IconButton>
            </div>
        </div>
    );
};

export default Carousel;
